import { and, eq, type InitialQueryBuilder } from "@tanstack/db";
import { DateTime, Duration } from "luxon";

import { store } from "@/store/store";
import { BroadcastAction, BroadcastRecipientList, ScheduleTrigger, WorkflowItem } from "@/store/types";
import { firstOccurrence, parseCronScheduledDate } from "@/utils/date";
import { formatLocalDate, formatZonedDate } from "@/utils/dateFormat";

export type ScheduledWorkflowItem = {
  workspaceId: string;
  workflowItemId: string | null;
  feedIds: string[];
  scheduledCron?: string | undefined;
  timezone?: string | undefined;
  scheduledDate?: string | undefined;
};

export const calculateCron = (date: Date, dayOfWeek: number, timezone: string) => {
  const { minute: mins, hour: hours } = DateTime.fromJSDate(date);
  const month = "*";
  const dayOfMonth = "?";
  const daysOfWeek = dayOfWeek.toString();
  const scheduledCron = `${mins} ${hours} ${dayOfMonth} ${month} ${daysOfWeek} *`;
  return {
    scheduledDate: "",
    scheduledCron,
    timezone,
  };
};

export const prettyCronDate = (scheduledTrigger, timezone) => {
  if (scheduledTrigger && timezone) {
    const hasCronSchedule = (scheduledTrigger as any)?.cronSchedule?.length > 0 && timezone?.length > 0;
    if (hasCronSchedule) {
      const parsedDate = parseCronScheduledDate((scheduledTrigger as any)?.cronSchedule.toString(), timezone);
      return formatZonedDate(parsedDate, timezone || "", "'Every' EEEE 'at' hh:mm a");
    } else if (scheduledTrigger?.oneTimeSchedule !== null) {
      return formatLocalDate(new Date(scheduledTrigger?.oneTimeSchedule.toString()), "MMM dd, yyyy 'at' hh:mm a");
    }
  }
};

export const calculateScheduledDateOrCron = ({
  date,
  dayOfWeek,
  doesRepeat,
}: {
  date: Date;
  dayOfWeek: number;
  doesRepeat: boolean;
}) => {
  if (!doesRepeat || typeof dayOfWeek !== "number" || isNaN(dayOfWeek)) {
    // date
    return date?.toISOString();
  }

  const { minute: mins, hour: hours } = DateTime.fromJSDate(date);
  const month = "*";
  const dayOfMonth = "?";
  const daysOfWeek = dayOfWeek.toString();
  return `${mins} ${hours} ${dayOfMonth} ${month} ${daysOfWeek} *`;
};

export const fetchScheduledDate = (scheduledTrigger: ScheduleTrigger) => {
  if (scheduledTrigger?.cronSchedule && scheduledTrigger?.timezone) {
    return parseCronScheduledDate(scheduledTrigger.cronSchedule, scheduledTrigger.timezone);
  }
  if (scheduledTrigger?.oneTimeSchedule) {
    return new Date(scheduledTrigger.oneTimeSchedule);
  }
  return null;
};

export const fullTranscriptionsQuery = (contentId: string) => (q: InitialQueryBuilder) =>
  q
    .from({ transcription: store.collection("transcription") })
    .where(({ transcription }) =>
      and(eq(transcription.contentId, contentId), eq(transcription.transcriptionType, "full")),
    );

export const rawFilesQuery = (contentId: string) => (q: InitialQueryBuilder) =>
  q.from({ file: store.collection("file") }).where(({ file }) => eq(file.contentId, contentId));

export const displayArtifactsForContentQuery = (contentId: string) => (q: InitialQueryBuilder) =>
  q
    .from({ displayArtifact: store.collection("displayArtifact") })
    .where(({ displayArtifact }) => eq(displayArtifact.contentId, contentId));

export const workflowMap = ({
  timezone,
  workflowItems,
  broadcasts,
  scheduleTriggers,
  broadcastRecipients,
}: {
  timezone: string;
  workflowItems: WorkflowItem[];
  broadcasts: BroadcastAction[];
  scheduleTriggers: ScheduleTrigger[];
  broadcastRecipients: BroadcastRecipientList[];
}) =>
  workflowItems
    ?.map((wi) => {
      const broadcast = broadcasts?.find((b) => b?.workflowItemId === wi?.id);
      const scheduledTrigger = broadcast?.id
        ? scheduleTriggers?.find((ots) => ots.broadcastActionId === broadcast?.id)
        : null;

      const recipients = broadcastRecipients
        ?.filter((br) => br.broadcastId === broadcast?.broadcastId)
        .map((br) => ({ id: br.feedId, title: br.title }));

      const maxRecipients = 10;
      const totalRecipients = recipients?.length ?? 0;
      const recipientMap =
        recipients
          ?.map((r) => r?.title)
          ?.filter((r) => r !== null)
          ?.slice(0, maxRecipients) ?? [];
      const andMore = totalRecipients > maxRecipients ? ` + ${totalRecipients - maxRecipients} more` : "";
      const prettyDate = prettyCronDate(scheduledTrigger, timezone);
      const scheduledDate = fetchScheduledDate(scheduledTrigger);

      return {
        ...wi,
        prettyDate,
        scheduledDate,
        broadcast,
        scheduledTrigger,
        recipients,
        recipientMap,
        andMore,
      };
    })
    ?.filter((w) => w.scheduledTrigger);

export const roundTimeToNearest15Minutes = (date: Date) => {
  const viewer = DateTime.fromJSDate(date);
  const step = Duration.fromObject({ minutes: 15 }).as("minutes");
  const remainder = viewer.minute % step;
  const minute = remainder >= step / 2 ? viewer.minute + (step - remainder) : viewer.minute - remainder;

  // Seconds and milliseconds go to 0 for a clean rounded time.
  return firstOccurrence(viewer.set({ minute, second: 0, millisecond: 0 })).toJSDate();
};

export const modalTabs = [
  { key: "media", text: "Add File", step: 1, active: false, disabled: false },
  { key: "generate", text: "Text", step: 1, active: false, disabled: false },
  { key: "date", text: "Date & Time", step: 2, active: false, disabled: false },
  { key: "channel", text: "Recipients", step: 3, active: false, disabled: false },
];
